import React, { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext();

const calculateTotal = (items) => {
  return items.reduce((sum, item) => sum + (Number(item.price) || 0) * item.quantity, 0);
};

const normalizeLegacyDonation = (item) => ({
  ...item,
  id: `donation-${item.db_id || item.id}`,
  itemType: 'donation',
  donationCardId: item.db_id || null,
  donationCardSlug: item.slug || item.id,
  donationType: item.type || 'donation',
  shippingRequired: Boolean(item.shippingRequired),
  taxable: item.type === 'merchandise',
  frequency: item.frequency || 'one-time',
  quantity: Math.max(1, Number(item.quantity) || 1),
});

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'INIT':
      return action.payload;
    case 'ADD': {
      const existing = state.items.find((i) => i.id === action.payload.id);
      let items;
      if (existing) {
        items = state.items.map((i) =>
          i.id === action.payload.id
            ? { ...i, quantity: i.quantity + action.payload.quantity }
            : i
        );
      } else {
        items = [...state.items, action.payload];
      }
      return { items, total: calculateTotal(items) };
    }
    case 'REMOVE': {
      const items = state.items.filter((i) => i.id !== action.payload);
      return { items, total: calculateTotal(items) };
    }
    case 'UPDATE': {
      const items = state.items
        .map((i) =>
          i.id === action.payload.id
            ? { ...i, quantity: Math.max(1, action.payload.quantity) }
            : i
        );
      return { items, total: calculateTotal(items) };
    }
    case 'CLEAR':
      return { items: [], total: 0 };
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(
    cartReducer,
    { items: [], total: 0 },
    (initial) => {
      try {
        const saved = localStorage.getItem('guhso-cart');
        const parsed = saved ? JSON.parse(saved) : initial;
        const currentItems = (parsed.items || []).map((item) => ({
          itemType: item.itemType || 'product',
          ...item,
          productId: item.itemType === 'donation' ? item.productId : (item.productId || item.id),
        }));
        const current = { items: currentItems, total: calculateTotal(currentItems) };
        const legacy = localStorage.getItem('guhso-donation-cart');

        if (!legacy) return current;

        const legacyItems = (JSON.parse(legacy)?.items || [])
          .filter((item) => item.frequency !== 'monthly')
          .map(normalizeLegacyDonation);
        const existingIds = new Set((current.items || []).map((item) => item.id));
        const items = [
          ...(current.items || []),
          ...legacyItems.filter((item) => !existingIds.has(item.id)),
        ];

        localStorage.removeItem('guhso-donation-cart');
        return { items, total: calculateTotal(items) };
      } catch (e) {
        // ignore
      }
      return initial;
    }
  );

  useEffect(() => {
    localStorage.setItem('guhso-cart', JSON.stringify(state));
  }, [state]);

  const addToCart = (item) => {
    dispatch({
      type: 'ADD',
      payload: {
        itemType: item.itemType || 'product',
        ...item,
        quantity: item.quantity || 1,
      },
    });
  };

  const removeFromCart = (id) => {
    dispatch({ type: 'REMOVE', payload: id });
  };

  const updateQuantity = (id, quantity) => {
    dispatch({ type: 'UPDATE', payload: { id, quantity } });
  };

  const clearCart = () => dispatch({ type: 'CLEAR' });

  const getItemCount = () =>
    state.items.reduce((count, item) => count + item.quantity, 0);

  const value = {
    ...state,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getItemCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
